import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Users, FileText, Calendar, Receipt, UserCog, 
  ChevronRight, ChevronLeft, X, Check, Sparkles,
  ClipboardList, Send, DollarSign
} from 'lucide-react';

const tutorialSteps = [
  {
    id: 'welcome',
    icon: Sparkles,
    title: 'Welcome to ContractorOS!',
    description: 'Let\'s take a quick tour to help you get started. This will only take a minute.',
    tip: 'You can skip this tutorial anytime and access help from the menu.',
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 'customers',
    icon: Users,
    title: 'Add Your Customers',
    description: 'Start by adding your customers. You can add their contact info, address, and notes.',
    tip: 'Go to Customers → Click "Add Customer" → Fill in the details',
    action: '/clients?action=new',
    color: 'from-emerald-500 to-emerald-600'
  },
  {
    id: 'estimates',
    icon: FileText,
    title: 'Create Estimates',
    description: 'Create professional estimates with line items, photos, and your branding. Send them via email instantly.',
    tip: 'Go to Estimates → "New Estimate" → Select customer → Add line items → Send',
    action: '/estimates/new',
    color: 'from-amber-500 to-amber-600'
  },
  {
    id: 'jobs',
    icon: Calendar,
    title: 'Schedule Jobs',
    description: 'When an estimate is approved, schedule the job. Track your work in the calendar view.',
    tip: 'Approved estimates → Click "Schedule Job" → Pick dates → Done!',
    action: '/jobs',
    color: 'from-purple-500 to-purple-600'
  },
  {
    id: 'invoices',
    icon: Receipt,
    title: 'Send Invoices',
    description: 'After completing a job, send an invoice. Track payments and outstanding balances.',
    tip: 'Jobs page → "Create Invoice" or Invoices → "New Invoice"',
    action: '/invoices',
    color: 'from-rose-500 to-rose-600'
  },
  {
    id: 'crew',
    icon: UserCog,
    title: 'Manage Your Crew',
    description: 'Add your team members. Assign them to jobs and track their work.',
    tip: 'Settings → Crew tab → Add crew members',
    action: '/settings',
    color: 'from-cyan-500 to-cyan-600'
  },
  {
    id: 'done',
    icon: Check,
    title: 'You\'re All Set!',
    description: 'That\'s the basics! You\'re ready to start running your business with ContractorOS.',
    tip: 'Need help? Click your profile icon for support options.',
    color: 'from-green-500 to-green-600'
  }
];

const OnboardingTutorial = ({ onComplete, onSkip, initialStep = 0 }) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();
  const { organization } = useAuth();

  const step = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    // Clear any saved step when completing
    if (organization?.id) {
      localStorage.removeItem(`tutorial_step_${organization.id}`);
    }
    setIsExiting(true);
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  const handleSkip = () => {
    // Clear any saved step when skipping
    if (organization?.id) {
      localStorage.removeItem(`tutorial_step_${organization.id}`);
    }
    setIsExiting(true);
    setTimeout(() => {
      onSkip();
    }, 300);
  };

  const handleTryIt = () => {
    if (step.action) {
      // Save current step so we can resume when user comes back
      if (organization?.id) {
        localStorage.setItem(`tutorial_step_${organization.id}`, String(currentStep));
        localStorage.setItem(`tutorial_paused_${organization.id}`, 'true');
      }
      // Navigate to the action but DON'T complete the tutorial
      setIsExiting(true);
      setTimeout(() => {
        navigate(step.action);
      }, 300);
    }
  };

  const StepIcon = step.icon;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ${isExiting ? 'opacity-0' : 'opacity-100'}`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} />
      
      {/* Modal */}
      <div className={`relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden transition-transform duration-300 ${isExiting ? 'scale-95' : 'scale-100'}`}>
        {/* Progress Bar */}
        <div className="h-1.5 bg-gray-100">
          <div 
            className="h-full bg-gradient-to-r from-primary to-blue-400 transition-all duration-500"
            style={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
          />
        </div>

        {/* Skip Button */}
        <button 
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-8 pt-6">
          {/* Icon */}
          <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6 mx-auto shadow-lg`}>
            <StepIcon className="w-10 h-10 text-white" strokeWidth={1.5} />
          </div>

          {/* Title & Description */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h2>
            <p className="text-gray-600 text-base leading-relaxed">{step.description}</p>
          </div>

          {/* Tip Box */}
          <div className="bg-slate-50 rounded-xl p-4 mb-8">
            <p className="text-sm text-slate-700 flex items-start gap-2">
              <span className="text-primary font-semibold shrink-0">Tip:</span>
              <span>{step.tip}</span>
            </p>
          </div>

          {/* Step Dots */}
          <div className="flex justify-center gap-2 mb-6">
            {tutorialSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentStep 
                    ? 'bg-primary w-8' 
                    : index < currentStep 
                      ? 'bg-primary/50' 
                      : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={handlePrev}
                className="flex-1 h-12 rounded-xl"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            
            {step.action && !isLastStep && (
              <Button
                variant="outline"
                onClick={handleTryIt}
                className="flex-1 h-12 rounded-xl border-primary text-primary hover:bg-primary/5"
              >
                Try It Now
              </Button>
            )}
            
            <Button
              onClick={handleNext}
              className="flex-1 h-12 rounded-xl bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-500/90"
            >
              {isLastStep ? (
                <>
                  Get Started <Check className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Skip Text */}
          {!isLastStep && (
            <button 
              onClick={handleSkip}
              className="w-full mt-4 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip tutorial
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial;
